import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  AnalyticsParams,
  AnalyticsReportConfigurationRequest,
  NpsCsatConfigRequest,
  PracticeListRequest,
  QuestionSetRequest,
  SurveyCharts,
  SurveyStats,
} from 'redux/schemas/api/analytics';
import {
  AnalyticsDashBoardType,
  AnalyticsPageType,
  AnalyticsState,
  ConfigurationType,
  NpsCsatConfig,
  NpsCsatSummary,
  ReportConfiguration,
} from 'redux/schemas/app/analytics';
import { BasicEnrollment } from 'redux/schemas/models/course';
import { QuizQuestion } from 'redux/schemas/models/quiz';
import t from 'react-translate';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { makeQueryParamString } from './helpers';
import { addAlertMessage } from './alert-messages';

export const getAnalyticsToken = createAsyncThunk(
  'GET_ANALYTICS_TAGS',
  getToken,
);

async function getToken(params: AnalyticsParams) {
  const { type, catalogId, institutionId, journeyCatalogId, pageIdKey, questionSetId, scenarioId, cohortCourseIds, programId } = params;
  let tokenUrl = '';
  if (type === AnalyticsDashBoardType.COURSE || type === AnalyticsDashBoardType.COHORTS) {
    tokenUrl = `/analytics/${institutionId}/course_analytics_access/${catalogId}.json`;
  }
  if (type === AnalyticsDashBoardType.PRACTICE_ANALYTICS) {
    tokenUrl = `analytics/practice_scenarios/${scenarioId}.json`;
  }
  if (type === AnalyticsDashBoardType.LEARNING_JOURNEY) {
    tokenUrl = `analytics/${institutionId}/journey_analytics_access/${journeyCatalogId}`;
  }
  if (type === AnalyticsDashBoardType.INSTITUTION) {
    tokenUrl = `analytics/${institutionId}/org_analytics_access.json`;
  }
  if (type === AnalyticsDashBoardType.LICENSE) {
    tokenUrl = pageIdKey === 'main' ? `analytics/${institutionId}/license_analytics_access.json` : `analytics/${institutionId}/merged_license_analytics_access.json`;
  }
  if (type === AnalyticsDashBoardType.QUESTION_SET) {
    tokenUrl = `/analytics/${institutionId}/courses/${catalogId}/question_sets/${questionSetId}.json`;
  }
  if (type === AnalyticsDashBoardType.COHORT_QUESTION_SET) {
    tokenUrl = `/analytics/${institutionId}/courses/${catalogId}/question_sets/cross_primary_and_cohorts/${questionSetId}.json`;
    return axios.get(tokenUrl, { params: cohortCourseIds.length > 0 ? { course_ids: cohortCourseIds.join(',') } : null }).then((response) => response.data.result);
  }
  if (type === AnalyticsDashBoardType.MENTORSHIP_PROGRAM) {
    tokenUrl = `/analytics/${institutionId}/mentorship_programs/${programId}/mentorship_program_access.json`;
  }
  return axios.get(tokenUrl).then((response) => response.data.result);
}

export const fetchQuestionSet = createAsyncThunk <AnalyticsState['questionSets'], QuestionSetRequest>(
  'FETCH_QUESTION_SET',
  async (params) => {
    const { institutionId, catalogId, filterByType } = params;
    const paramString = filterByType ? makeQueryParamString({ filterByType }) : '';
    const response = await axios.get(`/analytics/${institutionId}/courses/${catalogId}/question_sets?${paramString}`);
    return response.data.result;
  },
);

export const fetchCohortsQuestionSet = createAsyncThunk <AnalyticsState['questionSets'], QuestionSetRequest>(
  'FETCH_COHORTS_QUESTION_SET',
  async (params) => {
    const { institutionId, catalogId, filterByType } = params;
    const paramString = filterByType ? makeQueryParamString({ filterByType }) : '';
    const response = await axios.get(`/analytics/${institutionId}/courses/${catalogId}/question_sets/primary_course_quizes_and_surveys.json?${paramString}`);
    return response.data.result;
  },
);

export const fetchPracticeList = createAsyncThunk <AnalyticsState['practiceList'], PracticeListRequest>(
  'FETCH_PRACTICE_LIST',
  async ({ catalogId }) => {
    try {
      const response = await axios.get(`/analytics/courses/${catalogId}/course_scenarios`);
      return response.data.result;
    } catch {
      return [];
    }
  },
);


export const fetchCohorts = createAsyncThunk <BasicEnrollment[], { catalogId: string, institutionId: number, questionSetId: number }>(
  'FETCH_COHORTS_LIST',
  async ({ catalogId, institutionId, questionSetId }) => {
    try {
      const response = await axios.get(`/analytics/${institutionId}/primary_or_cohort_courses_as_admin/${catalogId}.json?question_set_id=${questionSetId}`);
      return response.data.result;
    } catch {
      return [];
    }
  },
);

export const fetchAccessibleCourses = createAsyncThunk <BasicEnrollment[], { institutionId: number, catalogId }>(
  'FETCH_ACCESSIBLE_COURSES',
  async ({ institutionId, catalogId }) => {
    try {
      const response = await axios.get(`/analytics/${institutionId}/accessible_courses/${catalogId}.json`);
      return response.data.result;
    } catch {
      return [];
    }
  },
);

export const fetchSurveyQuestions = createAsyncThunk<QuizQuestion[], { catalogId: string, questionSetId: number }>(
  'FETCH_SURVEY_QUESTIONS',
  async ({ catalogId, questionSetId }) => {
    try {
      const response = await axios.get(`/${catalogId}/question_sets/${questionSetId}/questions/quantitative_questions.json`);
      return response.data.result;
    } catch {
      return [];
    }
  },
);

export const addConfiguration = createAsyncThunk<void, AnalyticsReportConfigurationRequest>(
  'ADD_PRE_POST_CONFIGURATION',
  async ({ institutionId, preCourseCatalogId, ...configuration }, thunkAPI) => {
    try {
      const response = await axios.post(`/analytics/${institutionId}/courses/${preCourseCatalogId}/analytics_report_configurations`, configuration);
      return response.data.result;
    } catch (error) {
      thunkAPI.dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.OOPS(),
        message: t.FORM.ERROR_SOMETHING_WRONG(),
      }));
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const hasExistingConfiguration = createAsyncThunk<ReportConfiguration[], { institutionId: number, preCourseCatalogId: string, configurationType?: ConfigurationType }>(
  'HAS_EXISTING_CONFIGURATION',
  async ({ institutionId, preCourseCatalogId }) => {
    const response = await axios.get(`/analytics/${institutionId}/courses/${preCourseCatalogId}/analytics_report_configurations`);
    return response.data.result;
  },
);
export const fetchConfiguration = createAsyncThunk<ReportConfiguration, { institutionId: number, preCourseCatalogId: string, configurationId: number }>(
  'FETCH_CONFIGURATION',
  async ({ institutionId, preCourseCatalogId, configurationId }) => {
    const response = await axios.get(`/analytics/${institutionId}/courses/${preCourseCatalogId}/analytics_report_configurations/${configurationId}`);
    return response.data.result;
  },
);
export const updateConfiguration = createAsyncThunk<ReportConfiguration, AnalyticsReportConfigurationRequest>(
  'UPDATE_PRE_POST_CONFIGURATION',
  async ({ institutionId, preCourseCatalogId, configurationId, ...configuration }, thunkAPI) => {
    try {
      const response = await axios.put(`/analytics/${institutionId}/courses/${preCourseCatalogId}/analytics_report_configurations/${configurationId}`, configuration);
      return response.data.result;
    } catch (error) {
      thunkAPI.dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.OOPS(),
        message: t.FORM.ERROR_SOMETHING_WRONG(),
      }));
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const surveyCharts = createAsyncThunk<SurveyCharts, { institutionId: number, preCourseCatalogId: string, configurationId: number }>(
  'FETCH_SURVEY_CHARTS',
  async ({ institutionId, preCourseCatalogId, configurationId }) => {
    const response = await axios.get(`/analytics/${institutionId}/courses/${preCourseCatalogId}/pre_post_question_sets/${configurationId}/survey_charts.json`);
    return response.data.result;
  },
);

export const distributionCharts = createAsyncThunk<any, { institutionId: number, preCourseCatalogId: string, configurationId: number }>(
  'FETCH_DISTRIBUTION_CHARTS',
  async ({ institutionId, preCourseCatalogId, configurationId }) => {
    const response = await axios.get(`/analytics/${institutionId}/courses/${preCourseCatalogId}/pre_post_question_sets/${configurationId}/distribution_charts.json`);
    return response.data.result;
  },
);

export const surveyStats = createAsyncThunk<SurveyStats, { institutionId: number, preCourseCatalogId: string, configurationId: number }>(
  'FETCH_SURVEY_STATS',
  async ({ institutionId, preCourseCatalogId, configurationId }) => {
    const response = await axios.get(`/analytics/${institutionId}/courses/${preCourseCatalogId}/pre_post_question_sets/${configurationId}/survey_stats.json`);
    return response.data.result;
  },
);

export const fetchNpsCsatConfig = createAsyncThunk<NpsCsatConfig, QuestionSetRequest>(
  'FETCH_NPS_CSAT_CONFIG',
  async (params, thunkAPI) => {
    try {
      const { institutionId, catalogId, questionSetId } = params;
      const response = await axios.get(`/analytics/${institutionId}/courses/${catalogId}/question_sets/${questionSetId}/analytics_survey_configuration.json`);
      return response.data.result;
    } catch (error) {
      thunkAPI.dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.OOPS(),
        message: t.FORM.ERROR_SOMETHING_WRONG(),
      }));
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const addNpsCsatConfiguration = createAsyncThunk<NpsCsatConfig, NpsCsatConfigRequest>(
  'ADD_NPS_CSAT_CONFIGURATION',
  async (params, thunkAPI) => {
    try {
      const { institutionId, catalogId, questionSetId, npsQuestionId, csatQuestionId } = params;
      const configuration = {
        npsQuestionId,
        csatQuestionId,
      };
      const response = await axios.post(`/analytics/${institutionId}/courses/${catalogId}/question_sets/${questionSetId}/analytics_survey_configuration`, configuration);
      return response.data.result;
    } catch (error) {
      thunkAPI.dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.OOPS(),
        message: t.FORM.ERROR_SOMETHING_WRONG(),
      }));
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const updateNpsCsatConfiguration = createAsyncThunk<NpsCsatConfig, NpsCsatConfigRequest>(
  'UPDATE_NPS_CSAT_CONFIGURATION',
  async (params, thunkAPI) => {
    try {
      const { institutionId, catalogId, questionSetId, npsQuestionId, csatQuestionId } = params;
      const configuration = {
        npsQuestionId,
        csatQuestionId,
      };
      const response = await axios.patch(`/analytics/${institutionId}/courses/${catalogId}/question_sets/${questionSetId}/analytics_survey_configuration`, configuration);
      return response.data.result;
    } catch (error) {
      thunkAPI.dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.OOPS(),
        message: t.FORM.ERROR_SOMETHING_WRONG(),
      }));
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const fetchNpsCsatSummary = createAsyncThunk<NpsCsatSummary, QuestionSetRequest>(
  'FETCH_NPS_CSAT_SUMMARY',
  async (params, thunkAPI) => {
    try {
      const { institutionId, catalogId, questionSetId, type } = params;
      let requestUrl = `/analytics/${institutionId}/courses/${catalogId}/question_sets/${questionSetId}/nps_csat_summary.json`;
      if (type === AnalyticsPageType.COHORT_ANALYTICS)
        requestUrl = `/analytics/${institutionId}/courses/${catalogId}/question_sets/cross_primary_and_cohorts/${questionSetId}/nps_csat_summary.json`;
      const response = await axios.get(requestUrl);
      return response.data.result;
    } catch (error) {
      thunkAPI.dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.OOPS(),
        message: t.FORM.ERROR_SOMETHING_WRONG(),
      }));
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const fetchPreNpsCsatConfig = createAsyncThunk<NpsCsatConfig, QuestionSetRequest>(
  'FETCH_PRE_NPS_CSAT_CONFIG',
  async (params, thunkAPI) => {
    try {
      const { institutionId, catalogId, questionSetId } = params;
      const response = await axios.get(`/analytics/${institutionId}/courses/${catalogId}/question_sets/${questionSetId}/analytics_survey_configuration.json`);
      return response.data.result;
    } catch (error) {
      thunkAPI.dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.OOPS(),
        message: t.FORM.ERROR_SOMETHING_WRONG(),
      }));
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const resetQuestionSets = createAction('RESET_QUESTION_SETS');

export const setCurrentConfigurationId = createAction<number | null>('SET_CURRENT_CONFIGURATION_ID');
